import {
  Box,
  Dialog,
  List,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import { InvoiceResponse } from 'store/invoices/invoices.types'
import { useGetOrderByIdQuery } from 'store/orders/orders.api'
import { useSendInvoiceEmailHooks } from './invoice-table-email-dialog.hooks'
import InvoiceToEmail from './invoice-to-email/invoice-to-email.component'
import InvoiceFromEmail from './invoice-from-email/invoice-from-email.component'
import InvoiceEmailSubject from './invoice-email-subject/invoice-email-subject.component'
import InvoiceEmailMessage from './invoice-email-message/invoice-email-message.component'
import InvoiceEmailAttachments from './invoice-email-attachments/invoice-email-attachments.component'
import InvoiceEmailButtons from './invoice-email-buttons/invoice-email-buttons.component'
import TranspotsLoading from 'components/transpots-loading/transpots-loading.component'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'

type InvoiceTableEmailDialogProps = {
  open: boolean
  invoice: InvoiceResponse
  handleClose: () => void
}

const InvoiceTableEmailDialog = ({
  open,
  invoice,
  handleClose,
}: InvoiceTableEmailDialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const { data: order } = useGetOrderByIdQuery({
    orderId: parseInt(invoice.orderId) ?? skipToken,
  })

  const {
    toEmail,
    userEmail,
    factoringEmails,
    errorOnSending,
    setErrorOnSending,
    errorMessage,
    emailSendLoading,
    handleSendInvoice,
  } = useSendInvoiceEmailHooks(invoice, order, handleClose)

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <Box>
        <Typography sx={{ bgcolor: 'secondary.light', p: 2 }}>
          Send by Email / {invoice.userInvoiceId}
        </Typography>

        <List>
          <InvoiceFromEmail />
          <InvoiceToEmail
            defaultEmail={toEmail}
            errorOnSending={errorOnSending}
            userEmail={userEmail}
            factoringEmails={factoringEmails}
          />
          <InvoiceEmailSubject {...invoice} />
          <InvoiceEmailMessage {...invoice} />
        </List>

        <InvoiceEmailAttachments invoice={invoice} />
        <InvoiceEmailButtons
          handleClose={handleClose}
          handleSendInvoice={handleSendInvoice}
        />
      </Box>
      {emailSendLoading && <TranspotsLoading loadingText="Sending Email..." />}
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorOnSending}
        handleClose={() => setErrorOnSending(false)}
      />
    </Dialog>
  )
}

export default InvoiceTableEmailDialog
